import type { NavigateFunction } from "react-router-dom";
import { ROUTES } from "../config/routes";
import store from "../store/store";
import { signOut } from "../Features/Auth/AuthenticationSlice";
import { auth } from "../Features/config/firebase";

export const AUTH_REDIRECT_STORAGE_KEY = "authRedirectAfterLogin";

export type PostLoginRedirect = {
  pathname: string;
  search: string;
};

let navigateRef: NavigateFunction | null = null;
let redirectInProgress = false;

export function setAuthNavigate(fn: NavigateFunction): void {
  navigateRef = fn;
}

export function stashPostLoginRedirect(from: PostLoginRedirect): void {
  try {
    sessionStorage.setItem(AUTH_REDIRECT_STORAGE_KEY, JSON.stringify(from));
  } catch {
    // sessionStorage may be unavailable in some environments
  }
}

export function consumeStashedPostLoginRedirect(): PostLoginRedirect | null {
  try {
    const raw = sessionStorage.getItem(AUTH_REDIRECT_STORAGE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(AUTH_REDIRECT_STORAGE_KEY);
    const parsed = JSON.parse(raw) as PostLoginRedirect;
    if (typeof parsed?.pathname === "string") {
      return {
        pathname: parsed.pathname,
        search: typeof parsed.search === "string" ? parsed.search : "",
      };
    }
  } catch {
    sessionStorage.removeItem(AUTH_REDIRECT_STORAGE_KEY);
  }
  return null;
}

export function currentLocationRedirect(): PostLoginRedirect {
  return {
    pathname: window.location.pathname,
    search: window.location.search,
  };
}

/** True for expired/invalid tokens and auth middleware 403s — not arbitrary permission denials. */
export function isAuthApiError(error: unknown): boolean {
  const ax = error as {
    response?: { status?: number; data?: { error?: string; message?: string } };
    message?: string;
  };
  if (ax.message === "User not authenticated. Please login.") {
    return true;
  }
  const status = ax.response?.status;
  if (status === 401) return true;
  if (status === 403) {
    const text = `${ax.response?.data?.error ?? ""} ${ax.response?.data?.message ?? ""}`.toLowerCase();
    // Endpoint authorization failures are not session expiry — do not force logout.
    if (text.includes("admin role required")) {
      return false;
    }
    return (
      text.includes("token") ||
      text.includes("bearer") ||
      text.includes("authentication") ||
      text.includes("required role") ||
      text.includes("insufficient permissions")
    );
  }
  return false;
}

/** Clear session and send user to login, preserving return path for post-login navigation. */
export async function redirectToLogin(from?: PostLoginRedirect): Promise<void> {
  if (redirectInProgress) return;

  const target = from ?? currentLocationRedirect();
  if (target.pathname === ROUTES.LOGIN) return;

  redirectInProgress = true;
  stashPostLoginRedirect(target);

  try {
    await auth.signOut();
  } catch {
    // ignore sign-out errors during forced redirect
  }
  store.dispatch(signOut());

  if (navigateRef) {
    navigateRef(ROUTES.LOGIN, { state: { from: target }, replace: true });
  } else {
    window.location.assign(ROUTES.LOGIN);
  }

  window.setTimeout(() => {
    redirectInProgress = false;
  }, 3000);
}
